import StepUpDialog from './components/StepUpDialog' // W107 (§18)
import { lazy, Suspense } from 'react';
import Router from './routes';
import { Routes, Route, Navigate } from "react-router-dom"
import "./assets/css/App.css"
import "./assets/css/Loading.css"
import "./assets/css/date_picker.css"
import "./assets/css/droppable.css"
import "./assets/css/react_selector.css"
import "./assets/css/text.css"
import "./assets/scss/index.scss"
import 'react-quill/dist/quill.snow.css';
import { Tokens } from './constants';
import { getSecureItem, decodeJwtPayload } from './lib/secureStorage';
import PortfolioMain from "./pages/dashboard/portfolioMain/portfolioMain"
import Login from './pages/auth/Login';
import Register from './pages/auth/register';
import AdminUser from './pages/admin/User/AdminUser';
import DashboardMaster from './layout/dashboard/DashboardMaster';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import SsoComplete from './pages/auth/SsoComplete';
import ErrorBoundary from './components/common/ErrorBoundary';
import Loading from './shared/common/Loading';
import { useSelector } from 'react-redux';
import { canUseInvestmentWorkspace, homePathFor } from './hooks/useWorkspace';

// W61 (WSP-001): the landing page is the Investment portfolios table. A
// member who holds roles only in the Control workspace lands on the first
// Control screen their capabilities open (capability wave, 3 Oct 2026, R-08 —
// an attestor used to land on a dashboard they may not read). Until the
// organisation context has loaded the frame shows a loader rather than the
// table (the sidebar inside it is what loads that context, R-10).
const HomeRoute = () => {
  const context = useSelector((state) => state.organisationContext);
  if (!context?.loaded) {
    return <DashboardMaster><div className="flex justify-center items-center h-[60vh]"><Loading /></div></DashboardMaster>;
  }
  if (!canUseInvestmentWorkspace(context.roles)) {
    return <Navigate replace to={homePathFor(context.roles, context.capabilities)} />;
  }
  return <DashboardMaster><PortfolioMain /></DashboardMaster>;
};

// Public read-only policy document page — reachable without login, renders no
// app chrome (registered here beside /signin, outside the auth-guarded Router).
const SharedPolicyView = lazy(() => import('./pages/dashboard/policies/shared-view/SharedPolicyView'));

function App() {
  const token = getSecureItem(Tokens.ADMIN_TOKEN)
  let user = null;
  try { user = JSON.parse(getSecureItem(Tokens.ADMIN)); } catch { }
  const isToken = getSecureItem(Tokens.ADMIN_TOKEN);
  const tokenPayload = decodeJwtPayload(isToken);
  const role = tokenPayload?.role ?? user?.role;

  return (
    <ErrorBoundary>
      {/* W107 (§18): the step-up prompt, mounted once for every gated action. */}
      <StepUpDialog />
      <Routes>
        <Route path='/signin' element={isToken !== null ? role === 'admin' ? <Navigate replace to={'/admin'} /> : <Navigate replace to={'/'} /> : <Login />} />
        <Route path='/signup' element={token !== null ? <Navigate replace to={'/'} /> : <Register />} />
        <Route path='/reset-password' element={token !== null ? <Navigate replace to={'/'} /> : <ResetPassword />} />
        <Route path='/verify-email' element={token !== null ? <Navigate replace to={'/signup'} /> : <VerifyEmail />} />
        <Route path='/forgot-password' element={<Navigate replace to={'/signin'} />} />
        {/* ORG-008: the IdP redirects here after SSO; the page exchanges the
            callback cookie for the bearer token (and walks MFA if enabled). */}
        <Route path='/sso/complete' element={isToken !== null ? <Navigate replace to={'/'} /> : <SsoComplete />} />
        <Route path='/policies/shared/:token' element={
          <Suspense fallback={<div className="flex justify-center items-center h-screen bg-white"><Loading /></div>}>
            <SharedPolicyView />
          </Suspense>
        } />
        {role === 'user' ? <Route path='/' element={token !== null ? <HomeRoute /> : <Navigate replace to={'/signin'} />} /> :
          <Route path='/admin' element={token !== null ? <DashboardMaster><AdminUser /></DashboardMaster> : <Navigate replace to={'/signin'} />} />}
        <Route path='/*' element={<Router />} />
        <Route path='*' element={<Navigate replace to={"/"} />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;